import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "../context/AppContext";
import { Activity, ShieldCheck, TrendingUp, Cpu, Globe2, LineChart } from "lucide-react";

export default function Login() {
  const { authState } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    if (authState === "authed") navigate("/dashboard", { replace: true });
  }, [authState, navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const feats = [
    { icon: LineChart, t: "3-Pillar Factor Engine", d: "Fundamental, technical & macro scores — 0 to 100 composite." },
    { icon: Globe2, t: "India + Global Markets", d: "NSE/BSE equities, SGBs, G-Secs, US ETFs, gold & crypto." },
    { icon: Cpu, t: "AI Reasoning Layer", d: "Claude · GPT · Gemini thesis, risk & action mandates." },
    { icon: TrendingUp, t: "10-Year Wealth Forecast", d: "Step-up SIP engine with Monte-Carlo retirement bands." },
  ];

  return (
    <div className="min-h-screen bg-[#080b11] grid lg:grid-cols-2">
      {/* Left: brand + value */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 grid-bg overflow-hidden">
        <div className="absolute inset-0" style={{ background: "radial-gradient(900px 500px at 20% 0%, rgba(0,229,255,0.10), transparent 60%)" }} />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(0,229,255,0.12)", border: "1px solid rgba(0,229,255,0.4)" }}>
            <Activity className="w-5 h-5 text-[#00e5ff]" />
          </div>
          <div>
            <div className="font-semibold tracking-tight text-[#f8fafc]">Alpha-Orchestrator</div>
            <div className="overline">Quantitative Investment Engine</div>
          </div>
        </div>

        <div className="relative z-10">
          <h1 className="text-4xl xl:text-5xl font-semibold tracking-tight leading-[1.05] text-[#f8fafc]">
            Build serious wealth<br />with an institutional<br /><span className="text-[#00e5ff]">quant co-pilot.</span>
          </h1>
          <p className="mt-5 max-w-md text-sm text-[#94a3b8] leading-relaxed">
            Multi-asset screening, risk-parity rebalancing and AI-synthesised mandates across Indian and global markets — engineered for a disciplined 10-year horizon.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4 max-w-lg">
            {feats.map((f) => (
              <div key={f.t} className="ao-card p-4" data-testid={`feature-${f.t}`}>
                <f.icon className="w-4 h-4 text-[#00e5ff] mb-2" />
                <div className="text-sm font-medium text-[#f8fafc]">{f.t}</div>
                <div className="text-xs text-[#64748b] mt-1 leading-snug">{f.d}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-6 font-mono text-xs text-[#64748b]">
          <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#00e676] live-dot" />NSE/BSE</span>
          <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#00e676] live-dot" />NASDAQ</span>
          <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#00e676] live-dot" />CRYPTO · LIVE</span>
        </div>
      </div>

      {/* Right: auth */}
      <div className="flex items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(0,229,255,0.12)", border: "1px solid rgba(0,229,255,0.4)" }}>
              <Activity className="w-5 h-5 text-[#00e5ff]" />
            </div>
            <div className="font-semibold tracking-tight">Alpha-Orchestrator</div>
          </div>

          <div className="ao-card p-8">
            <div className="overline mb-2">Private Access</div>
            <h2 className="text-2xl font-semibold tracking-tight text-[#f8fafc]">Sign in to your terminal</h2>
            <p className="text-sm text-[#94a3b8] mt-2 leading-relaxed">
              Your portfolio, positions and projections are private to your account.
            </p>

            <button
              data-testid="google-login-btn"
              onClick={handleLogin}
              className="mt-7 w-full flex items-center justify-center gap-3 rounded-lg bg-white text-[#0b0f17] font-medium py-3 hover:bg-[#e8eef7] transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              Continue with Google
            </button>

            <div className="mt-6 flex items-start gap-2 text-xs text-[#64748b]">
              <ShieldCheck className="w-4 h-4 mt-0.5 text-[#34d399]" />
              <span>Secured by Emergent Auth. Session-encrypted · 7-day sign-in.</span>
            </div>
          </div>

          <p className="text-center text-xs text-[#64748b] mt-6 font-mono">
            Not investment advice · For personal portfolio research
          </p>
        </motion.div>
      </div>
    </div>
  );
}
